import {useEffect, useRef, useState} from 'react'

// https://developers.cloudflare.com/turnstile/get-started/client-side-rendering/widget-configurations/
export interface TurnstileRenderOptions {
  sitekey: string
  action: string
  callback: (token: string) => void
  'error-callback': (code: string) => boolean
  'expired-callback': () => void
  'timeout-callback': () => void
  'before-interactive-callback': () => void
  appearance?: 'always' | 'execute' | 'interaction-only'
  theme?: 'light' | 'dark' | 'auto'
  size?: 'normal' | 'flexible' | 'compact'
  retry?: 'auto' | 'never' | 'manual'
}

interface TurnstileApi {
  render: (el: HTMLElement, opts: TurnstileRenderOptions) => string
  reset: (widgetId: string) => void
  remove: (widgetId: string) => void
}

declare global {
  interface Window {
    turnstile?: TurnstileApi
    // Named by the `onload` parameter on the script URL; Turnstile invokes it once its API object
    // is attached.
    onloadTurnstileCallback?: () => void
  }
}

// Explicit rendering: https://developers.cloudflare.com/turnstile/get-started/client-side-rendering/
const TURNSTILE_SCRIPT_SRC =
  'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit&onload=onloadTurnstileCallback'

const TURNSTILE_SCRIPT_SELECTOR = `script[src="${TURNSTILE_SCRIPT_SRC}"]`

// Cloudflare's own `error-callback` codes are numeric strings; this one is ours, for the case where
// the widget never got far enough to produce one.
const SCRIPT_LOAD_ERROR_CODE = 'script-load-failed'

const SCRIPT_LOAD_TIMEOUT_MS = 15_000

// Once the script has executed, the only thing still outstanding is the API object it attaches on
// its way through, so a much shorter wait is enough -- and the caller learns sooner when what came
// back was a stub rather than api.js.
const API_ATTACH_TIMEOUT_MS = 3_000

// With `retry: 'auto'` Turnstile re-runs a failed challenge itself, calling `error-callback` again
// each time; this is how many of those failures pass before the hook stops waiting on it and hands
// the error to the caller. The budget covers one challenge: solving, expiring or timing out starts
// a fresh one, which gets the whole budget rather than what the last one left.
const MAX_AUTO_RETRIES = 4

// Cloudflare marks these as not retryable; every attempt returns the same code.
// https://developers.cloudflare.com/turnstile/troubleshooting/client-side-errors/error-codes/
const PERMANENT_ERROR_CODES = new Set(['110100', '110110', '110200', '200100', '400020', '400070'])

export type TurnstileChallenge = {kind: 'pending'} | {kind: 'solved'; token: string} | {kind: 'failed'; code: string}

const PENDING: TurnstileChallenge = {kind: 'pending'}

interface TurnstileWidgetOptions {
  enabled: boolean
  sitekey: string
  appearance: 'always' | 'interaction-only'
  size: 'normal' | 'flexible'
  theme: 'light' | 'auto'
  onToken?: (token: string) => void
  // Fires only once the challenge has failed for good -- Turnstile's own retries are spent first.
  onError?: (code: string) => void
  onExpired?: () => void
  onTimeout?: () => void
  onInteractive?: () => void
}

type TurnstileCallbacks = Pick<
  TurnstileWidgetOptions,
  'onToken' | 'onError' | 'onExpired' | 'onTimeout' | 'onInteractive'
>

// A token is single-use, so a form has to `reset` after every sign-in attempt to get another.
export function useTurnstileWidget(options: TurnstileWidgetOptions) {
  const {enabled, sitekey, appearance, size, theme} = options
  const containerRef = useRef<HTMLDivElement>(null)
  const widgetRef = useRef<{turnstile: TurnstileApi; widgetId: string}>(undefined)
  const [challenge, setChallenge] = useState<TurnstileChallenge>(PENDING)
  // Turnstile holds the callbacks it was rendered with, so they read the caller's latest ones
  // through this ref instead of re-rendering the widget whenever a callback identity changes.
  const callbacksRef = useRef<TurnstileCallbacks>(options)
  useEffect(() => {
    callbacksRef.current = options
  })
  const failureCountRef = useRef(0)

  useEffect(() => {
    if (!enabled) return

    // Cleared by `fail`, so whichever path reports a failure first is the only one that does.
    let widgetDeadline: number | undefined

    // A token already handed over stays usable; only a challenge still pending becomes a failure.
    function fail(code: string) {
      window.clearTimeout(widgetDeadline)
      setChallenge((current) => (current.kind === 'solved' ? current : {kind: 'failed', code}))
      callbacksRef.current.onError?.(code)
    }

    // Dropping the dead tag keeps the guard below -- which skips appending a second script -- from
    // costing the next mount the whole load deadline before it can give up again.
    function failScriptLoad() {
      document.querySelector(TURNSTILE_SCRIPT_SELECTOR)?.remove()
      fail(SCRIPT_LOAD_ERROR_CODE)
    }

    function renderWidget() {
      if (widgetRef.current) return
      const container = containerRef.current
      if (!container) throw new Error('useTurnstileWidget: containerRef is not attached to an element')
      const turnstile = window.turnstile
      if (!turnstile) {
        failScriptLoad()
        return
      }

      const widgetId = turnstile.render(container, {
        sitekey,
        action: 'login',
        callback: (token) => {
          failureCountRef.current = 0
          setChallenge({kind: 'solved', token})
          callbacksRef.current.onToken?.(token)
        },
        'error-callback': (code) => {
          if (!PERMANENT_ERROR_CODES.has(code)) {
            failureCountRef.current += 1
            // Returning false leaves the error with Turnstile, which retries it; returning true
            // says the caller has taken it over, which is what stops the retries.
            if (failureCountRef.current <= MAX_AUTO_RETRIES) return false
          }
          fail(code)
          return true
        },
        'expired-callback': () => {
          turnstile.reset(widgetId)
          failureCountRef.current = 0
          setChallenge(PENDING)
          callbacksRef.current.onExpired?.()
        },
        // Reruns the challenge without relying on Turnstile's own `refresh-timeout` default to do it.
        'timeout-callback': () => {
          turnstile.reset(widgetId)
          failureCountRef.current = 0
          callbacksRef.current.onTimeout?.()
        },
        'before-interactive-callback': () => callbacksRef.current.onInteractive?.(),
        appearance,
        size,
        theme,
        retry: 'auto'
      })
      widgetRef.current = {turnstile, widgetId}
    }

    function removeWidget() {
      const widget = widgetRef.current
      if (widget) {
        widget.turnstile.remove(widget.widgetId)
        widgetRef.current = undefined
      }
      failureCountRef.current = 0
      setChallenge(PENDING)
    }

    if (window.turnstile) {
      renderWidget()
      return removeWidget
    }

    // Must be set before the script is appended -- Turnstile resolves the name at execution time.
    window.onloadTurnstileCallback = renderWidget

    // A stalled request -- captive portal, a blocked domain, a dropped connection -- fires neither
    // the load callback nor onerror, so a deadline is the only signal the caller will get.
    function failLoad() {
      if (!widgetRef.current) failScriptLoad()
    }
    widgetDeadline = window.setTimeout(failLoad, SCRIPT_LOAD_TIMEOUT_MS)

    // Strict mode's double mount, or a form remounting the widget before the script has loaded: the
    // pending script's onload callback renders this widget, and a second copy would only race it.
    const script = document.querySelector(TURNSTILE_SCRIPT_SELECTOR) ? undefined : document.createElement('script')

    function cleanUp() {
      delete window.onloadTurnstileCallback
      window.clearTimeout(widgetDeadline)
      // The script outlives the mount that appended it; a later mount owns its own deadline.
      if (script) {
        script.onload = null
        script.onerror = null
      }
      removeWidget()
    }

    if (script) {
      script.src = TURNSTILE_SCRIPT_SRC
      script.async = true
      // The load event only says the bytes ran, so this tightens the deadline rather than rendering:
      // the API object may not be attached yet.
      script.onload = () => {
        window.clearTimeout(widgetDeadline)
        widgetDeadline = window.setTimeout(failLoad, API_ATTACH_TIMEOUT_MS)
      }
      script.onerror = () => failScriptLoad()
      document.head.appendChild(script)
    }

    return cleanUp
  }, [enabled, sitekey, appearance, size, theme])

  // A failure stands until the rerun challenge produces a token, so a widget that already gave up
  // does not hold the form again.
  function reset() {
    const widget = widgetRef.current
    if (widget) widget.turnstile.reset(widget.widgetId)
    failureCountRef.current = 0
    setChallenge((current) => (current.kind === 'solved' ? PENDING : current))
  }

  return {containerRef, reset, challenge}
}
